import { useEffect } from "react";
import { Download, FileIcon, X } from "lucide-react";
import { Button } from "./ui/button";

const IMAGE_EXTS = ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp"];

export function Lightbox({ file, onClose }) {
  useEffect(() => {
    const handler = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  if (!file) return null;
  const ext = (file.original || "").split(".").pop().toLowerCase();
  const isImage = IMAGE_EXTS.includes(ext);
  const isPdf = ext === "pdf";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/70 backdrop-blur-sm p-4 sm:p-8"
      onClick={onClose}
      data-testid="lightbox-overlay"
    >
      <div
        className="bg-white rounded-2xl border border-[#E5E5E5] max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox"
      >
        <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-[#E5E5E5] shrink-0">
          <span className="text-sm font-medium truncate" data-testid="lightbox-filename">{file.original}</span>
          <div className="flex items-center gap-2 shrink-0">
            <a href={file.url} download={file.original} data-testid="lightbox-download">
              <Button variant="outline" size="sm" className="rounded-full">
                <Download className="h-3.5 w-3.5 mr-1.5" /> Download
              </Button>
            </a>
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="lightbox-close">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="flex-1 overflow-auto flex items-center justify-center bg-neutral-50 p-4 min-h-[280px]">
          {isImage ? (
            <img src={file.url} alt={file.original} className="max-w-full max-h-[72vh] object-contain rounded-lg" data-testid="lightbox-image" />
          ) : isPdf ? (
            <iframe src={file.url} title={file.original} className="w-full h-[72vh] rounded-lg bg-white" data-testid="lightbox-pdf" />
          ) : (
            <div className="text-center py-16" data-testid="lightbox-no-preview">
              <FileIcon className="h-12 w-12 text-neutral-300 mx-auto" />
              <p className="mt-4 text-sm text-neutral-500">Preview not available for this file type.</p>
              <p className="text-xs text-neutral-400 mt-1">Use Download to save the file.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
